'use client'

import { memo, ReactNode, useEffect, useRef, useState } from 'react'
import { CmsFaqGroup, CmsFaqSubGroup } from '../../../domain/faqGroups'
import Swiper from 'swiper'
import { EffectFade, Navigation } from 'swiper/modules'
import { handleError } from '../../../core/hook/utils'
import { ArrowNavigationLeft, ArrowNavigationRight } from '../SliderUtils/ArrowNavigation'
import { gtmPush } from '../../utils/gtmHelpers'

// keep memo function, because we load HTML from Magento BO and we need to add some HTML markups
// to the loaded HTML. And when rerender component the added markups are lost
const FaqSubGroupAccordion = memo((
  { subGroup, group }: { subGroup: CmsFaqSubGroup, group: CmsFaqGroup }
) => {
  const subGroupRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const ACTIVE_CLASSNAME = 'active'
    const QUESTION_HEADER_CLASSNAME = 'ui-accordion-header'
    if (!subGroupRef.current) {
      return;
    }
    const subGroupElem = subGroupRef.current;
    function handleQuestionHeaderClick(ev: MouseEvent) {
      const elem = ev.target as HTMLElement;
      if (elem.classList.contains(QUESTION_HEADER_CLASSNAME)) {
        const isActive = elem.classList.contains(ACTIVE_CLASSNAME)
        if (!isActive) {
          const activeElemList = subGroupElem.querySelectorAll<HTMLElement>(`.${ACTIVE_CLASSNAME}`)
          activeElemList.forEach((elem) => {
            elem.classList.remove(ACTIVE_CLASSNAME)
          })

          gtmPush({
            event: 'open_faq',
            question_title: elem?.textContent?.trim(),
            question_category: group.name,
          })
        }

        elem.classList.toggle(ACTIVE_CLASSNAME)
        elem.nextElementSibling?.classList.toggle(ACTIVE_CLASSNAME)
      }
    }
    subGroupElem.addEventListener('click', handleQuestionHeaderClick);
    return () => {
      subGroupElem.removeEventListener('click', handleQuestionHeaderClick);
    }
  }, [group])

  return (
    <div
      className='faq-accordion-subGroup-content'
      dangerouslySetInnerHTML={{ __html: subGroup.content }}
      ref={subGroupRef}
    />
  )
})

const FaqAccordion = ({
  faqGroups,
  cmsContent,
}: {
  faqGroups: CmsFaqGroup[]
  cmsContent: ReactNode
}) => {
  const [selectedFaqGroup, setSelectedFaqGroup] = useState(faqGroups[0])
  const [selectedFaqSubGroup, setSelectedFaqSubGroup] = useState<number | null>(null)

  useEffect(() => {
    const swiper = new Swiper('.swiper', {
      watchSlidesProgress: true,
      modules: [Navigation, EffectFade],
      allowTouchMove: true,
      loop: false,
      spaceBetween: 30,
      slidesPerView: 'auto',
      navigation: {
        prevEl: '.faq-groups-arrow-left',
        nextEl: '.faq-groups-arrow-right',
        disabledClass: 'disabled',
      },
    })

    return () => {
      try {
        swiper.destroy?.()
      } catch (error) {
        // TODO workaround
        handleError(error, false)
      }
    }
  }, [faqGroups])
  return (
    <div className='faq-page-group'>
      <div className='container-slider-nav-faq'>
        <div className='swiper'>
          <div className='swiper-wrapper'>
            {faqGroups.map((faqGroup) => (
              <div className='swiper-slide' key={faqGroup.id}>
                <a
                  href='#'
                  className={`group-link ${faqGroup.id === selectedFaqGroup.id ? 'active' : ''}`}
                  data-group-id={faqGroup.id}
                  onClick={(e) => {
                    e.preventDefault()
                    setSelectedFaqGroup(faqGroup)
                    setSelectedFaqSubGroup(null)
                  }}>
                  {faqGroup.name}
                </a>
              </div>
            ))}
          </div>
        </div>

        <div className='arrow-navigation'>
          <div className='faq-groups-arrow faq-groups-arrow-left'>
            <ArrowNavigationLeft />
          </div>
          <div className='faq-groups-arrow faq-groups-arrow-right'>
            <ArrowNavigationRight />
          </div>
        </div>
      </div>
      <div className='mageprince-faq-collection '>
        <div className='faq-content'>
          <div className='faq-accordion'>
            {selectedFaqGroup.items.map((subGroup) => (
              <div
                className={`faq-accordion-subGroup ${
                  subGroup.id === selectedFaqSubGroup ? 'active' : ''
                }`}
                key={subGroup.id}>
                <h3
                  className='faq-accordion-subGroup-title'
                  onClick={() => {
                    gtmPush({
                      event: 'open_faq',
                      question_title: subGroup.name,
                      question_category: selectedFaqGroup.name,
                    })
                    setSelectedFaqSubGroup((prevState) =>
                      prevState === subGroup.id ? null : subGroup.id,
                    )
                  }}>
                  <span>{subGroup.name}</span>{' '}
                </h3>
                <FaqSubGroupAccordion
                  group={selectedFaqGroup}
                  subGroup={subGroup}
                />
              </div>
            ))}
          </div>
        </div>

        {cmsContent}
      </div>
    </div>
  )
}

export default FaqAccordion
